import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { User, History, LogOut, CreditCard, Menu, HelpCircle, Sparkles, Gift } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useRoles } from "@/hooks/useRoles";
import { Link } from "react-router-dom";
import { ThemeToggle } from "./ThemeToggle";
import { MobileSidebar } from "./MobileSidebar";
import { TutorialHighlight } from "./tutorial/TutorialHighlight";
import { getTrialStatus, getABTestStatus } from "@/lib/utils";

interface HeaderProps {
  onShowHistory?: () => void;
  onShowSubscription?: () => void;
  onShowChangelog?: () => void;
}

export function Header({ onShowHistory, onShowSubscription, onShowChangelog }: HeaderProps) {
  const { user, signIn, signOut, subscription } = useAuth();
  const { isAdmin } = useRoles();

  const isSubscribed = !!subscription?.subscribed;
  const plan = subscription?.plan ?? null;
  const remaining = subscription?.monthly_credits ?? null;
  const planMax = (() => {
    const p = (plan || '').toLowerCase();
    if (p.includes('pro')) return 50;
    if (p.includes('básico') || p.includes('basic')) return 5;
    if (p.includes('micro')) return 0;
    return null;
  })();
  const pct = planMax != null && remaining != null ? Math.max(0, Math.min(100, (remaining / planMax) * 100)) : null;
  const barColorClass = pct == null ? 'bg-primary' : pct < 20 ? 'bg-destructive' : pct < 50 ? 'bg-warning' : 'bg-primary';

  // Trial status for free users  
  const { isTrialActive, isTrialExpired, daysLeft } = getTrialStatus(subscription);
  
  // A/B test status
  const { isDiscountGroup, discountActive, discountHoursLeft, discountCode } = getABTestStatus(subscription);

  // Determine what badge to show
  const showTrialBadge = !isSubscribed && !isDiscountGroup && (isTrialActive || isTrialExpired);
  const showDiscountBadge = !isSubscribed && isDiscountGroup && discountActive;

  return (
    <header className="border-b bg-background/80 backdrop-blur-md sticky top-0 z-50">
      <div className="container max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
          <img src="/inteligencialegal-logo-square.png" alt="DoctIA Logo" className="h-8 w-8" />
          <span className="text-2xl font-bold text-foreground">DoctIA</span>
        </Link>

        <div className="hidden sm:flex items-center gap-4">
          {user ? (
            <>
              <ThemeToggle />
              {!isSubscribed && (
                <>
                  {/* Trial badge for trial group */}
                  {showTrialBadge && isTrialActive && (
                    <Badge variant="outline" className="text-xs">
                      Prueba: {daysLeft} día{daysLeft !== 1 ? 's' : ''}
                    </Badge>
                  )}
                  {showTrialBadge && isTrialExpired && (
                    <Badge variant="destructive" className="text-xs">
                      Prueba expirada
                    </Badge>
                  )}
                  
                  {/* Discount badge for discount group */}
                  {showDiscountBadge && (
                    <Link to={`/planes?code=${discountCode}`}>
                      <Badge variant="default" className="text-xs bg-gradient-to-r from-primary to-accent cursor-pointer animate-pulse hover:animate-none">
                        <Gift className="h-3 w-3 mr-1" />
                        40% OFF - {discountHoursLeft}h
                      </Badge>
                    </Link>
                  )}
                  
                  <Link to="/planes">
                    <Button variant="default" size="sm" className="shadow-glow">
                      <Sparkles className="h-4 w-4 mr-1" />
                      Suscribite
                    </Button>
                  </Link>
                </>
              )}
              <Link to="/buscar">
                <Button variant="ghost" size="sm">Buscar</Button>
              </Link>
              <TutorialHighlight 
                step="view_history" 
                tooltipContent="Hacé clic para ver tu historial"
                tooltipSide="bottom"
              >
                <Link to="/historial">
                  <Button variant="ghost" size="sm">Historial</Button>
                </Link>
              </TutorialHighlight>
              <Link to="/ayuda">
                <Button variant="ghost" size="sm">Ayuda</Button>
              </Link>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    className={`rounded-full ${isSubscribed ? 'ring-2 ring-primary ring-offset-2 ring-offset-background shadow-glow' : 'ring-2 ring-border ring-offset-2 ring-offset-background'}`}
                  >
                    <Menu className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-popover border-border">
                  <div className="flex items-center justify-start gap-2 p-2">
                    <div className="flex flex-col space-y-1 leading-none">
                      {user.user_metadata?.name && (
                        <p className="font-medium">{user.user_metadata.name}</p>
                      )}
                      <p className="w-[200px] truncate text-sm text-muted-foreground">
                        {user.email}
                      </p>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/historial">
                      <div className="flex items-center">
                        <History className="mr-2 h-4 w-4" />
                        <span>Historial</span>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                  {onShowChangelog && (
                    <DropdownMenuItem onClick={onShowChangelog}>
                      <Sparkles className="mr-2 h-4 w-4" />
                      <span>Ver Novedades</span>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  {isAdmin && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin">
                        <div className="flex items-center">
                          <User className="mr-2 h-4 w-4" />
                          <span>Administración</span>
                        </div>
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {onShowSubscription ? (
                    <DropdownMenuItem onClick={onShowSubscription}>
                      <CreditCard className="mr-2 h-4 w-4" />
                      <span>Gestionar Suscripción</span>
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem asChild>
                      <Link to="/suscripcion">
                        <div className="flex items-center">
                          <CreditCard className="mr-2 h-4 w-4" />
                          <span>Gestionar Suscripción</span>
                        </div>
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={signOut} className="text-destructive focus:text-destructive">
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Cerrar Sesión</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <ThemeToggle />
              <Link to="/planes">
                <Button variant="ghost" size="sm">Planes</Button>
              </Link>
              <Link to="/auth">
                <Button variant="outline" size="sm">
                  Iniciar Sesión
                </Button>
              </Link>
            </>
          )}
        </div>

        <div className="flex sm:hidden items-center gap-2">
          <ThemeToggle />
          <MobileSidebar
            user={user}
            isSubscribed={isSubscribed}
            isAdmin={isAdmin}
            showDiscountBadge={showDiscountBadge}
            discountCode={discountCode}
            discountHoursLeft={discountHoursLeft}
            signOut={signOut}
            onShowSubscription={onShowSubscription}
            onShowChangelog={onShowChangelog}
          />
        </div>
      </div>
    </header>
  );
}
